import React, { useEffect, useState } from "react"
import {
  Box,
  Text,
  HStack,
  VStack,
  SimpleGrid,
  Input,
  Textarea,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Button,
  IconButton,
  Spinner,
  Icon,
  Image,
  Checkbox,
  Collapse,
} from "@chakra-ui/react"
import { TopicPill } from "./TopicPill"
import { DifficultyPill } from "./DifficultyPill"
import { ChevronRightIcon, ChevronLeftIcon } from "@chakra-ui/icons"
import { HiOutlineTrash, HiOutlinePlus, HiOutlineCheck, HiChevronUp, HiChevronDown, HiChevronLeft, HiChevronRight } from "react-icons/hi2"
import { useNavigate, useLocation } from "react-router-dom"
import { BASE_API_URL } from "../../Components/config"
import { PALETTES, useClassroomColorMode } from "../classroomColors"
import { buildMatcher, formatSeries, haystack } from "../../Components/searchMatch"
import ClassroomDatePicker, { formatDMY } from "../ClassroomDatePicker"
import "../classroom-theme.css"

const API = `${BASE_API_URL}/api/v1`

const DIFF_DOT = { EASY: "#34d399", MEDIUM: "#f0b93a", HARD: "#f87171" }

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "")

// Difficulty is stored only as a 1-10 score. Easy/Medium/Hard is a display
// concept that lives here, so the bands can be retuned without touching the
// backend or relabelling the question bank.
const MIN_SCORE = 1
const MAX_SCORE = 10
const EASY_MAX = 3
const MEDIUM_MAX = 7

const DIFFICULTY_BANDS = {
  easy: { label: "EASY", min: MIN_SCORE, max: EASY_MAX },
  medium: { label: "MEDIUM", min: EASY_MAX + 1, max: MEDIUM_MAX },
  hard: { label: "HARD", min: MEDIUM_MAX + 1, max: MAX_SCORE },
}

const difficultyLabel = (question) => {
  const score = question?.difficulty_score
  if (score == null) return null // not labelled yet
  if (score <= EASY_MAX) return DIFFICULTY_BANDS.easy.label
  if (score <= MEDIUM_MAX) return DIFFICULTY_BANDS.medium.label
  return DIFFICULTY_BANDS.hard.label
}

// Expand the selected bands into the explicit scores the API filters on.
const selectedDifficultyScores = (difficulty) => {
  const scores = []
  Object.entries(DIFFICULTY_BANDS).forEach(([key, band]) => {
    if (!difficulty[key]) return
    for (let s = band.min; s <= band.max; s++) scores.push(s)
  })
  return scores
}

// Render question text with each [IMAGE_N] marker replaced inline by the
// matching image_N, mirroring SolvePaper. Shared by the list and student views.
const buildQuestionNodes = (question, p, { maxH = "260px" } = {}) => {
  const rawText = question?.question_text || ""
  const imageProps = {
    display: "block", my: "10px", maxH, maxW: "100%", borderRadius: "8px",
    border: "1px solid", borderColor: p.border, background: "#ffffff",
  }
  const regex = /\[IMAGE_(\d+)\]/g
  const nodes = []
  const referenced = new Set()
  let last = 0, m, k = 0

  while ((m = regex.exec(rawText)) !== null) {
    if (m.index > last) {
      nodes.push(<Text as="span" key={`t${k}`} whiteSpace="pre-wrap">{rawText.slice(last, m.index)}</Text>)
    }
    const n = parseInt(m[1], 10)
    referenced.add(n)
    if (question?.[`image_${n}`]) {
      nodes.push(<Image key={`i${k}`} src={question[`image_${n}`]} alt={`Image ${n}`} {...imageProps} />)
    }
    last = regex.lastIndex
    k++
  }
  if (last < rawText.length) {
    nodes.push(<Text as="span" key={`t${k}`} whiteSpace="pre-wrap">{rawText.slice(last)}</Text>)
  }
  // Images not referenced by a placeholder are still shown.
  ;[1, 2, 3, 4, 5].forEach((n) => {
    if (question?.[`image_${n}`] && !referenced.has(n)) {
      nodes.push(<Image key={`x${n}`} src={question[`image_${n}`]} alt={`Image ${n}`} {...imageProps} />)
    }
  })
  return nodes
}

// Same answer-format parsing SolvePaper uses to decide multiple-choice options.
const parseAnswerFormat = (answerFormat) => {
  if (!answerFormat) return []
  if (answerFormat.includes("[DISPLAY OPTIONS]")) {
    return [
      { label: "A", text: "" }, { label: "B", text: "" },
      { label: "C", text: "" }, { label: "D", text: "" },
    ]
  }
  const regex = /\[(\w+)\](.*?)(?=\[\w+\]|$)/gs
  const options = []
  let match
  while ((match = regex.exec(answerFormat)) !== null) {
    options.push({ label: match[1], text: match[2].trim() })
  }
  return options
}

export function CustomPaperPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    subjectGuid,
    courseGuid,
    classroomName,
    userType,
    paperType = "QUIZ",
    // Set from a session's post-class tab: the work is linked to that session
    // rather than to the course's default topic.
    courseSegmentGuid = null,
    sessionName = null,
  } = location.state || {}

  const { isDark } = useClassroomColorMode()
  const p = isDark ? PALETTES.dark : PALETTES.light

  // Leaving the builder returns to the classroom it was started from.
  const onClose = () =>
    navigate("/classroom/teacher/class", {
      state: { courseGuid, classroomName, userType: userType || "INSTRUCTOR" },
    })

  const [step, setStep] = useState(0)
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [topics, setTopics] = useState([])
  const [selectedTopicIds, setSelectedTopicIds] = useState([])
  const [difficulty, setDifficulty] = useState({ easy: false, medium: false, hard: false })
  const [paperLength, setPaperLength] = useState(35)
  const [dueDate, setDueDate] = useState("")
  const [loadingPhase, setLoadingPhase] = useState("idle")

  // "auto" generates from filters; "manual" lets the teacher pick questions.
  const [buildMode, setBuildMode] = useState("auto")
  const [browseSearch, setBrowseSearch] = useState("")
  const [browseResults, setBrowseResults] = useState([])
  const [browseLoading, setBrowseLoading] = useState(false)
  const [browseError, setBrowseError] = useState(null)
  // Where the questions on offer come from: the whole bank, filtered by topic
  // and text, or one paper the teacher has opened.
  const [questionSource, setQuestionSource] = useState("bank") // "bank" | "paper"
  const [paperSearch, setPaperSearch] = useState("")
  const [paperResults, setPaperResults] = useState([])
  const [papersLoading, setPapersLoading] = useState(false)
  const [openPaper, setOpenPaper] = useState(null)

  // Draft paper being previewed (created but not yet assigned)
  const [draftPaper, setDraftPaper] = useState(null)
  const [paperQuestions, setPaperQuestions] = useState([]) // [{guid(link), question}]
  const [previewError, setPreviewError] = useState(null)
  const [candidates, setCandidates] = useState([])
  const [showCandidates, setShowCandidates] = useState(false)
  const [expandedQ, setExpandedQ] = useState(null)
  // Collapse state lives here, not in GroupCard: GroupCard is redefined on each
  // render, so state held inside it would reset constantly.
  const [groupOpen, setGroupOpen] = useState({})
  const [topicsOpen, setTopicsOpen] = useState(false)
  const [previewView, setPreviewView] = useState("list") // "list" | "student"

  // Assignment targeting
  const [students, setStudents] = useState([])
  const [assignMode, setAssignMode] = useState("all") // "all" | "selected"
  const [selectedStudentGuids, setSelectedStudentGuids] = useState([])

  const token = localStorage.getItem("accessToken")
  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` }

  useEffect(() => {
    if (!subjectGuid) return
    async function loadTopics() {
      try {
        const res = await fetch(`${API}/subject-general-topics/with-topical-papers/by-subject-guid/${subjectGuid}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const data = await res.json()
        setTopics(Array.isArray(data) ? data : [])
      } catch (e) {
        setTopics([])
      }
    }
    loadTopics()
  }, [subjectGuid])

  useEffect(() => {
    if (!courseGuid) return
    async function loadStudents() {
      try {
        const res = await fetch(`${API}/user-course-links/students-by-course-guid/${courseGuid}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok) setStudents(await res.json())
      } catch (e) {
        setStudents([])
      }
    }
    loadStudents()
  }, [courseGuid])

  const toggleStudent = (guid) => {
    setSelectedStudentGuids((prev) =>
      prev.includes(guid) ? prev.filter((g) => g !== guid) : [...prev, guid]
    )
  }

  const studentName = (s) =>
    [s.first_name, s.last_name].filter(Boolean).join(" ").trim() || s.email

  const toggleTopic = (id) => {
    setSelectedTopicIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    )
  }

  const toggleDifficulty = (level) => {
    setDifficulty((prev) => ({ ...prev, [level]: !prev[level] }))
  }

  const questionCriteria = (numQuestions) => ({
    subject_guid: subjectGuid,
    topic_guids: [],
    subject_general_topic_guids: selectedTopicIds,
    difficulty_score_list: selectedDifficultyScores(difficulty),
    mark_list: [],
    num_questions: numQuestions,
    exam_series_year_list: [],
    exam_series_month_list: [],
    order_by_list: [],
  })

  const requestBodyCreatePaper = () => ({
    name: title,
    code: title.replace(/\s+/g, "_").toUpperCase(),
    type: paperType,
    title,
    description,
    subject_guid: subjectGuid,
    // course_guid intentionally omitted: the paper is only linked to the
    // course at assign time, after the teacher has previewed the questions.
    due_date: dueDate ? new Date(dueDate).toISOString() : null,
    question_criteria: questionCriteria(paperLength),
  })

  // ---- draft lifecycle -------------------------------------------------

  const discardDraft = async (paper, questions) => {
    // Best-effort cleanup: remove question links first so the paper row can
    // be deleted, then the paper itself.
    if (!paper?.guid) return
    try {
      for (const link of questions || []) {
        await fetch(`${API}/custom-paper-questions/${link.guid}`, {
          method: "DELETE",
          headers: authHeaders,
        })
      }
      await fetch(`${API}/custom-papers/${paper.guid}`, { method: "DELETE", headers: authHeaders })
    } catch (e) {
      // Not fatal: an unlinked draft paper is never shown to students.
    }
  }

  const resetDraftState = () => {
    setDraftPaper(null)
    setPaperQuestions([])
    setCandidates([])
    setShowCandidates(false)
    setPreviewError(null)
    setExpandedQ(null)
    setAssignMode("all")
    setSelectedStudentGuids([])
    setBrowseResults([])
    setBrowseSearch("")
    setBrowseError(null)
    setBuildMode("auto")
  }

  const generateAndPreview = async () => {
    setLoadingPhase("generating")
    setPreviewError(null)
    try {
      // Regenerating? Discard the previous draft first.
      if (draftPaper) {
        await discardDraft(draftPaper, paperQuestions)
        resetDraftState()
      }

      const res = await fetch(`${API}/custom-papers/with-filters`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(requestBodyCreatePaper()),
      })
      if (!res.ok) throw new Error(`Create failed (${res.status})`)
      const paper = await res.json()
      setDraftPaper(paper)

      const qRes = await fetch(`${API}/custom-paper-questions/by-custom-paper-guid/${paper.guid}`, {
        headers: authHeaders,
      })
      if (qRes.ok) {
        const links = await qRes.json()
        setPaperQuestions(links)
      } else {
        setPaperQuestions([])
        setPreviewError(
          "The paper was generated, but the question preview could not be loaded. You can still assign it, or go back and try again."
        )
      }
      setStep(2)
    } catch (e) {
      console.error(e)
      setPreviewError("Something went wrong while generating the paper. Please try again.")
    } finally {
      setLoadingPhase("idle")
    }
  }

  // A question group (e.g. 3, 3a, 3b, 3bi) is indivisible - the whole group is
  // added or removed together, never an individual part.
  const refetchPaperQuestions = async () => {
    const res = await fetch(`${API}/custom-paper-questions/by-custom-paper-guid/${draftPaper.guid}`, {
      headers: authHeaders,
    })
    if (res.ok) setPaperQuestions(await res.json())
  }

  // ---- manual build ----------------------------------------------------

  // Manual mode starts from an empty paper (num_questions: 0), which the
  // teacher then fills by adding whole question groups.
  const startManualBuild = async () => {
    setLoadingPhase("generating")
    setPreviewError(null)
    try {
      if (!draftPaper) {
        const res = await fetch(`${API}/custom-papers/with-filters`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({
            ...requestBodyCreatePaper(),
            question_criteria: { ...questionCriteria(0) },
          }),
        })
        if (!res.ok) throw new Error(`Create failed (${res.status})`)
        setDraftPaper(await res.json())
        setPaperQuestions([])
      }
      setStep(1)
      searchQuestionBank()
    } catch (e) {
      console.error(e)
      setPreviewError("Could not start the paper. Please try again.")
    } finally {
      setLoadingPhase("idle")
    }
  }

  const searchQuestionBank = async () => {
    setBrowseLoading(true)
    setBrowseError(null)
    try {
      const res = await fetch(`${API}/questions/query/groups-with-filters`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          ...questionCriteria(40),
          search_text: browseSearch.trim() || null,
        }),
      })
      if (!res.ok) throw new Error("search failed")
      setBrowseResults(await res.json())
    } catch (e) {
      console.error(e)
      setBrowseResults([])
      setBrowseError("Couldn't search the question bank. Please try again.")
    } finally {
      setBrowseLoading(false)
    }
  }

  // The subject's papers are fetched once and searched in the browser from
  // then on, which is how the student search works: results narrow as you
  // type instead of on a button, and a subject is a small enough list that
  // going back to the server per keystroke would buy nothing.
  const loadPapers = async () => {
    if (!subjectGuid) return
    setPapersLoading(true)
    setBrowseError(null)
    try {
      const params = new URLSearchParams({ subject_guid: subjectGuid, limit: "200" })
      const res = await fetch(`${API}/past-papers/search/?${params}`, { headers: authHeaders })
      if (!res.ok) throw new Error("paper load failed")
      setPaperResults(await res.json())
    } catch (e) {
      console.error(e)
      setPaperResults([])
      setBrowseError("Couldn't load the papers. Please try again.")
    } finally {
      setPapersLoading(false)
    }
  }

  // Opening a paper reuses the same question search the bank does, narrowed to
  // that paper - so the results are whole question groups in the same shape,
  // and everything below (the cards, adding, the added-already state) is
  // untouched by which source they came from.
  const openPaperQuestions = async (paper) => {
    setOpenPaper(paper)
    setBrowseLoading(true)
    setBrowseError(null)
    try {
      const res = await fetch(`${API}/questions/query/groups-with-filters`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          ...questionCriteria(40),
          // The teacher has chosen the paper; the topic and difficulty filters
          // they set for the bank should not also hide questions in it.
          subject_general_topic_guids: [],
          difficulty_score_list: [],
          past_paper_guids: [paper.guid],
        }),
      })
      if (!res.ok) throw new Error("paper questions failed")
      setBrowseResults(await res.json())
    } catch (e) {
      console.error(e)
      setBrowseResults([])
      setBrowseError("Couldn't load that paper's questions. Please try again.")
    } finally {
      setBrowseLoading(false)
    }
  }

  const closeOpenPaper = () => {
    setOpenPaper(null)
    setBrowseResults([])
  }

  const chooseSource = (source) => {
    if (source === questionSource) return
    setQuestionSource(source)
    setBrowseResults([])
    setBrowseError(null)
    setOpenPaper(null)
    if (source === "paper") {
      if (!paperResults.length) loadPapers()
    } else {
      searchQuestionBank()
    }
  }

  const removeGroup = async (groupGuid) => {
    const previous = paperQuestions
    setPaperQuestions((prev) => prev.filter((l) => l.question_group_guid !== groupGuid))
    try {
      const res = await fetch(
        `${API}/custom-paper-questions/by-group/${draftPaper.guid}/${groupGuid}`,
        { method: "DELETE", headers: authHeaders }
      )
      if (!res.ok) throw new Error("remove failed")
    } catch (e) {
      console.error(e)
      setPaperQuestions(previous) // restore on failure
    }
  }

  const loadCandidates = async () => {
    setShowCandidates(true)
    if (candidates.length) return
    try {
      const res = await fetch(`${API}/questions/query/groups-with-filters`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(questionCriteria(paperLength + 20)),
      })
      if (!res.ok) throw new Error("candidate fetch failed")
      const groups = await res.json()
      const inPaper = new Set(paperQuestions.map((l) => l.question_group_guid))
      setCandidates(groups.filter((g) => !inPaper.has(g.group_guid)).slice(0, 12))
    } catch (e) {
      setCandidates([])
    }
  }

  const addGroup = async (group) => {
    if (!draftPaper) return
    setCandidates((prev) => prev.filter((g) => g.group_guid !== group.group_guid))
    try {
      const res = await fetch(`${API}/custom-paper-questions/by-group`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          custom_paper_guid: draftPaper.guid,
          question_group_guid: group.group_guid,
        }),
      })
      if (!res.ok) throw new Error("add failed")
      await refetchPaperQuestions()
    } catch (e) {
      console.error(e)
      setCandidates((prev) => [group, ...prev]) // restore on failure
    }
  }

  const handleAssign = async () => {
    if (!draftPaper) return
    setLoadingPhase("assigning")
    try {
      // Same condition the backend uses to decide who the assignment goes to
      // (see UserPaperAttemptRouter's for-course endpoint): a real, non-empty
      // selection targets those students; anything else means everyone.
      // Recorded on the link itself so a student who joins the course later
      // can be checked against it - only an "everyone" assignment should be
      // backfilled onto them, never one the teacher aimed at specific people.
      const assignedToAll = !(assignMode === "selected" && selectedStudentGuids.length)

      // Link the paper to the course (carries the due date), mirroring what
      // the previous single-shot flow created.
      await fetch(`${API}/course-segment-paper-links/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          custom_paper_guid: draftPaper.guid,
          course_guid: courseGuid,
          course_segment_guid: courseSegmentGuid,
          due_date: dueDate ? new Date(dueDate).toISOString() : null,
          assign_to_all_students: assignedToAll,
        }),
      })

      const attemptBody = {
        completion_status: "INCOMPLETE",
        start_date_time: new Date().toISOString(),
        updated_date_time: new Date().toISOString(),
        total_scored_marks: 0,
        user_guid: "00000000-0000-0000-0000-000000000000",
        course_guid: courseGuid,
        custom_paper_guid: draftPaper.guid,
      }
      // Targeted assignment: send the chosen students; omit for everyone.
      if (!assignedToAll) {
        attemptBody.user_guids = selectedStudentGuids
      }

      await fetch(`${API}/user-paper-attempts/for-course/${courseGuid}`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(attemptBody),
      })

      // Returning to the classroom remounts it, so the assigned-work panel
      // refetches and shows the new paper.
      resetDraftState()
      setStep(0)
      onClose()
    } catch (e) {
      console.error(e)
      setPreviewError("Assigning failed. Please try again.")
    } finally {
      setLoadingPhase("idle")
    }
  }

  const handleClose = () => {
    if (loadingPhase !== "idle") return
    // Leaving with an unassigned draft: clean it up in the background.
    if (draftPaper) {
      const paper = draftPaper
      const questions = paperQuestions
      discardDraft(paper, questions)
    }
    resetDraftState()
    setStep(0)
    onClose()
  }

  const handleBackFromPreview = () => {
    // Keep the draft; if they regenerate, it is discarded first.
    setStep(1)
  }

  // ---- styles ----------------------------------------------------------

  const card = {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid",
    borderColor: p.border,
    background: p.surface,
    padding: "24px",
  }
  const cardTitle = { color: p.text, fontSize: "14px", fontWeight: 600, marginBottom: "16px" }
  const fieldLabel = { color: p.textSub, fontSize: "12px", fontWeight: 500, marginBottom: "8px" }
  const inputStyle = {
    borderRadius: "8px",
    border: "1px solid",
    borderColor: p.inputBorder,
    background: p.inputBg,
    color: p.text,
    fontSize: "13px",
    _placeholder: { color: p.textMuted },
    _hover: { borderColor: p.textMuted },
    _focus: { borderColor: p.accent, boxShadow: `0 0 0 1px ${p.accent}` },
  }

  const totalMarks = paperQuestions.reduce(
    (sum, l) => sum + (l.question?.total_mark || 0),
    0
  )

  // The paper's links are flat; display them as whole questions (groups) so a
  // question and all of its parts always read - and act - as one unit.
  const paperGroups = (() => {
    const out = []
    const index = {}
    paperQuestions.forEach((link) => {
      const g = link.question_group_guid
      if (!index[g]) {
        index[g] = { group_guid: g, questions: [] }
        out.push(index[g])
      }
      if (link.question) index[g].questions.push(link.question)
    })
    out.forEach((g) => g.questions.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
    return out
  })()

  const assignDisabled =
    !draftPaper ||
    paperQuestions.length === 0 ||
    (assignMode === "selected" && selectedStudentGuids.length === 0)

  const assignLabel =
    assignMode === "selected"
      ? `Assign to ${selectedStudentGuids.length} student${selectedStudentGuids.length === 1 ? "" : "s"}`
      : "Assign to everyone"

  // ---- renders ---------------------------------------------------------

  const renderLoadingState = () => {
    const text =
      loadingPhase === "generating"
        ? "Generating your paper…"
        : loadingPhase === "assigning"
        ? "Assigning to students…"
        : ""
    return (
      <Box width="100%" minHeight="233px" display="flex" justifyContent="center" alignItems="center">
        <VStack spacing="12px">
          <Spinner size="lg" color={p.accent} />
          <Text color={p.text} fontSize="14px" fontWeight={500}>{text}</Text>
        </VStack>
      </Box>
    )
  }

  const renderStepOne = () => (
    <VStack spacing="24px" align="stretch" width="100%">
      <Box {...card}>
        <Text {...cardTitle}>Custom paper information</Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px">
          <VStack align="stretch" spacing="16px">
            <Box>
              <Text {...fieldLabel}>Title</Text>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Cell biology quiz"
                height="38px"
                width="100%"
                {...inputStyle}
              />
            </Box>
          </VStack>
          <Box>
            <Text {...fieldLabel}>Description</Text>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What should students focus on?"
              width="100%"
              height="110px"
              resize="none"
              {...inputStyle}
            />
          </Box>
        </SimpleGrid>
      </Box>

      <Box {...card}>
        <Text {...cardTitle}>How do you want to build it?</Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
          {[
            {
              key: "auto",
              title: "Generate for me",
              desc: "Pick topics and difficulty, and we'll build the paper automatically.",
            },
            {
              key: "manual",
              title: "Choose questions myself",
              desc: "Search or filter the question bank and pick each question.",
            },
          ].map((opt) => {
            const active = buildMode === opt.key
            return (
              <Box
                key={opt.key}
                as="button"
                type="button"
                textAlign="left"
                padding="14px"
                borderRadius="10px"
                border="1px solid"
                borderColor={active ? p.accent : p.border}
                background={active ? p.accentSoft : p.surface}
                boxShadow={active ? `0 0 0 1px ${p.accent}` : "none"}
                transition="all 0.15s"
                _hover={{ borderColor: active ? p.accent : p.textMuted }}
                onClick={() => setBuildMode(opt.key)}
              >
                <Text color={active ? p.accentSoftText : p.text} fontSize="13.5px" fontWeight={600}>
                  {opt.title}
                </Text>
                <Text color={p.textSub} fontSize="12px" mt="3px" lineHeight="1.45">
                  {opt.desc}
                </Text>
              </Box>
            )
          })}
        </SimpleGrid>
      </Box>

      <Box {...card}>
        <Text {...cardTitle}>
          {buildMode === "manual" ? "Filter by topic (optional)" : "Select topics"}
        </Text>
        {topics.length === 0 ? (
          <Text color={p.textMuted} fontSize="13px">No topics available for this subject yet.</Text>
        ) : (
          <Box display="flex" flexWrap="wrap" gap="8px">
            {topics.map((topic) => (
              <TopicPill
                key={topic.guid}
                label={topic.name}
                isSelected={selectedTopicIds.includes(topic.guid)}
                onClick={() => toggleTopic(topic.guid)}
              />
            ))}
          </Box>
        )}
      </Box>

      <HStack width="100%" justifyContent="flex-end">
        <Button
          variant="ghost"
          rightIcon={<ChevronRightIcon />}
          onClick={() => (buildMode === "manual" ? startManualBuild() : setStep(1))}
          color={p.text}
          fontSize="14px"
          fontWeight={500}
          _hover={{ bg: p.hover }}
        >
          Next
        </Button>
      </HStack>
    </VStack>
  )

  const renderStepTwo = () => (
    <VStack spacing="24px" align="stretch" width="100%">
      <Box {...card}>
        <Text {...cardTitle}>Question difficulty & paper length</Text>

        <VStack align="flex-start" spacing="24px">
          <Box>
            <Text {...fieldLabel}>Question difficulty</Text>
            <HStack spacing="12px">
              <DifficultyPill level="easy" label="Easy" isSelected={difficulty.easy} onClick={() => toggleDifficulty("easy")} />
              <DifficultyPill level="medium" label="Medium" isSelected={difficulty.medium} onClick={() => toggleDifficulty("medium")} />
              <DifficultyPill level="hard" label="Hard" isSelected={difficulty.hard} onClick={() => toggleDifficulty("hard")} />
            </HStack>
          </Box>

          <Box width="100%">
            <Text {...fieldLabel}>Paper length</Text>
            <Box width="100%" position="relative">
              <Slider min={0} max={100} value={paperLength} onChange={setPaperLength}>
                <SliderTrack height="4px" borderRadius="24px" background={p.track}>
                  <SliderFilledTrack background={p.accent} />
                </SliderTrack>
                <SliderThumb boxSize="15px" background="#FFFFFF" boxShadow="0 0 5.5px rgba(0,0,0,0.35)" />
              </Slider>
              <HStack width="100%" justifyContent="space-between" marginTop="6px">
                <Text color={p.textSub} fontSize="11px" fontWeight={500}>0</Text>
                <Text color={p.text} fontSize="12px" fontWeight={600}>{paperLength} questions</Text>
                <Text color={p.textSub} fontSize="11px" fontWeight={500}>100</Text>
              </HStack>
            </Box>
          </Box>

          <Box>
            <Text {...fieldLabel}>Due date</Text>
            <ClassroomDatePicker value={dueDate} onChange={setDueDate} p={p} />
          </Box>
        </VStack>
      </Box>

      {previewError && (
        <Text color={p.accentSoftText} fontSize="13px">{previewError}</Text>
      )}

      <HStack width="100%" justifyContent="space-between" alignItems="center">
        <Button
          variant="ghost"
          leftIcon={<ChevronLeftIcon />}
          onClick={() => setStep(0)}
          color={p.textSub}
          fontSize="14px"
          fontWeight={500}
          _hover={{ bg: p.hover }}
        >
          Back
        </Button>
        <Button
          onClick={generateAndPreview}
          variant="unstyled"
          display="inline-flex"
          alignItems="center"
          gap="6px"
          px="20px"
          height="40px"
          borderRadius="full"
          background={p.accent}
          color={p.onAccent}
          fontSize="13px"
          fontWeight={600}
          _hover={{ background: p.accentHover }}
        >
          Generate & preview <ChevronRightIcon />
        </Button>
      </HStack>
    </VStack>
  )

  const QuestionRow = ({ index, question, badgeRight, onAction, actionIcon, actionLabel, actionColor, inline = false }) => {
    const rawText = question?.question_text || ""
    // Collapsed preview: strip the [IMAGE_N] markers so the text reads cleanly.
    const cleanText = rawText.replace(/\[IMAGE_\d+\]/g, " ").replace(/\s+/g, " ").trim()
    const hasImages = [1, 2, 3, 4, 5].some((n) => question?.[`image_${n}`])
    const canExpand = Boolean(cleanText) || hasImages
    const isExpanded = expandedQ === question?.guid

    // Expanded view: text with [IMAGE_N] markers replaced inline.
    const renderBody = () => {
      const nodes = buildQuestionNodes(question, p)
      return nodes.length ? nodes : <Text as="span" color={p.textMuted}>(no content)</Text>
    }

    // Inline mode: just the question body, used inside a group card where the
    // group header already owns the numbering and the add/remove action.
    if (inline) {
      return (
        <Box cursor={canExpand ? "pointer" : "default"} onClick={() => canExpand && setExpandedQ(isExpanded ? null : question.guid)}>
          {isExpanded ? (
            <Box color={p.text} fontSize="12.5px" lineHeight="19px">{renderBody()}</Box>
          ) : (
            <Text color={p.text} fontSize="12.5px" lineHeight="19px" noOfLines={2}>
              {cleanText || "(image based question)"}
            </Text>
          )}
          <HStack spacing="10px" mt="4px">
            {question?.total_mark > 0 && (
              <Text color={p.textSub} fontSize="10.5px" fontWeight={600}>
                {question.total_mark} {question.total_mark === 1 ? "mark" : "marks"}
              </Text>
            )}
            {difficultyLabel(question) && (
              <HStack spacing="4px">
                <Box boxSize="6px" borderRadius="full" background={DIFF_DOT[difficultyLabel(question)] || DIFF_DOT.MEDIUM} />
                <Text color={p.textSub} fontSize="10.5px">{cap(difficultyLabel(question))}</Text>
              </HStack>
            )}
            {hasImages && !isExpanded && (
              <Text color={p.textMuted} fontSize="10.5px">has image · tap to view</Text>
            )}
          </HStack>
        </Box>
      )
    }

    return (
        <HStack
          align="flex-start"
          spacing="12px"
          padding="14px 16px"
          borderRadius="10px"
          border="1px solid"
          borderColor={p.border}
          background={p.surface}
          width="100%"
        >
          <Text color={p.textMuted} fontSize="12px" fontWeight={600} minWidth="24px" pt="2px" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {String(index + 1).padStart(2, "0")}
          </Text>
          <Box flex="1" minWidth="0" cursor={canExpand ? "pointer" : "default"} onClick={() => canExpand && setExpandedQ(isExpanded ? null : question.guid)}>
            {isExpanded ? (
              <Box color={p.text} fontSize="13px" lineHeight="20px">{renderBody()}</Box>
            ) : (
              <Text color={p.text} fontSize="13px" lineHeight="20px" noOfLines={2}>
                {cleanText || "(image based question)"}
              </Text>
            )}
            <HStack spacing="10px" mt="6px">
              {question?.total_mark > 0 && (
                <Text color={p.textSub} fontSize="11px" fontWeight={600}>
                  {question.total_mark} {question.total_mark === 1 ? "mark" : "marks"}
                </Text>
              )}
              {difficultyLabel(question) && (
                <HStack spacing="4px">
                  <Box boxSize="6px" borderRadius="full" background={DIFF_DOT[difficultyLabel(question)] || DIFF_DOT.MEDIUM} />
                  <Text color={p.textSub} fontSize="11px">{cap(difficultyLabel(question))}</Text>
                </HStack>
              )}
              {hasImages && !isExpanded && (
                <Text color={p.textMuted} fontSize="11px">has image · tap to view</Text>
              )}
            </HStack>
          </Box>
          <IconButton
            aria-label={actionLabel}
            icon={<Icon as={actionIcon} boxSize="15px" />}
            size="sm"
            variant="ghost"
            color={actionColor}
            _hover={{ bg: p.hover }}
            onClick={onAction}
          />
        </HStack>
    )
  }

  // A whole exam question: header + every part. Added/removed as one unit.
  // `showNumber` is off for question-bank results: a bank question has no
  // number until it is placed in a paper, so only part letters are shown.
  const GroupCard = ({
    group, index, onAction, actionIcon, actionLabel, actionColor,
    actionDisabled = false, showNumber = true, listKey = "list", defaultOpen = true,
  }) => {
    const questions = group.questions || []
    const marks = questions.reduce((s, q) => s + (q?.total_mark || 0), 0)
    const displayNumber = index + 1
    const partLabel = (q) => {
      const part = `${q.letter || ""}${q.letter_part || ""}`
      if (!showNumber) return part
      return `${displayNumber}${part}`
    }

    const key = `${listKey}:${group.group_guid}`
    const isOpen = key in groupOpen ? groupOpen[key] : defaultOpen
    const toggle = () => setGroupOpen((prev) => ({ ...prev, [key]: !isOpen }))

    return (
      <Box border="1px solid" borderColor={p.border} borderRadius="10px" background={p.surface} width="100%">
        <HStack justifyContent="space-between" alignItems="center" padding="10px 14px">
          <HStack
            spacing="10px"
            minWidth="0"
            flex="1"
            cursor="pointer"
            onClick={toggle}
          >
            <Icon
              as={isOpen ? HiChevronUp : HiChevronDown}
              boxSize="15px"
              color={p.textMuted}
              flexShrink={0}
            />
            {showNumber && (
              <Text color={p.text} fontSize="13px" fontWeight={600}>
                Question {displayNumber}
              </Text>
            )}
            <Box background={p.surfaceSubtle} border="1px solid" borderColor={p.border} color={p.textSub} fontSize="10.5px" fontWeight={600} px="8px" py="2px" borderRadius="full">
              {questions.length} part{questions.length === 1 ? "" : "s"}
            </Box>
            {marks > 0 && (
              <Text color={p.textSub} fontSize="11px" fontWeight={600}>
                {marks} {marks === 1 ? "mark" : "marks"}
              </Text>
            )}
          </HStack>
          <IconButton
            aria-label={actionLabel}
            icon={<Icon as={actionIcon} boxSize="15px" />}
            size="sm"
            variant="ghost"
            color={actionColor}
            _hover={{ bg: actionDisabled ? "transparent" : p.hover }}
            onClick={actionDisabled ? undefined : onAction}
            isDisabled={actionDisabled}
            flexShrink={0}
          />
        </HStack>

        <Collapse in={isOpen} animateOpacity>
          <VStack spacing="0" align="stretch" borderTop="1px solid" borderColor={p.border}>
            {questions.map((q) => {
              const label = partLabel(q)
              return (
                <HStack key={q.guid} align="flex-start" spacing="10px" padding="10px 14px" borderBottom="1px solid" borderColor={p.border} _last={{ borderBottom: "none" }}>
                  {label && (
                    <Text color={p.textMuted} fontSize="11px" fontWeight={600} minWidth="34px" pt="2px">
                      {label}
                    </Text>
                  )}
                  <Box flex="1" minWidth="0">
                    <QuestionRow question={q} index={-1} inline />
                  </Box>
                </HStack>
              )
            })}
          </VStack>
        </Collapse>
      </Box>
    )
  }

  const renderManualBuild = () => {
    const addedGroups = new Set(paperQuestions.map((l) => l.question_group_guid))

    // Same matcher the student search uses: every term has to land somewhere
    // in the paper's text, so "paper 2 may 2024" works in any order and a
    // paper is not something you have to know the code of to find.
    const matchesPaper = buildMatcher(paperSearch)
    const visiblePapers = paperResults.filter((paper) =>
      matchesPaper(
        haystack([
          paper.name,
          paper.code,
          paper.paper_number ? `paper ${paper.paper_number}` : null,
          paper.paper_variant,
          formatSeries(paper.exam_series_month, paper.exam_series_year),
        ])
      )
    )

    return (
      <VStack spacing="24px" align="stretch" width="100%">
        <Box {...card}>
          <Text {...cardTitle}>Find questions</Text>

          {/* Two ways in: the bank as one pool, or a specific paper opened and
              picked from - which is how teachers tend to think about it. */}
          <HStack spacing="6px" mb="14px" background={p.surfaceSubtle} padding="4px" borderRadius="full" width="fit-content">
            {[
              { key: "bank", label: "Question bank" },
              { key: "paper", label: "By paper" },
            ].map((opt) => (
              <Button
                key={opt.key}
                variant="unstyled"
                height="30px"
                px="16px"
                borderRadius="full"
                fontSize="12.5px"
                fontWeight={600}
                bg={questionSource === opt.key ? p.surface : "transparent"}
                color={questionSource === opt.key ? p.text : p.textMuted}
                boxShadow={questionSource === opt.key ? "sm" : "none"}
                onClick={() => chooseSource(opt.key)}
              >
                {opt.label}
              </Button>
            ))}
          </HStack>

          {questionSource === "paper" ? (
            openPaper ? (
              <HStack spacing="8px" mb="14px" justifyContent="space-between">
                <HStack spacing="8px" minWidth={0}>
                  <Button
                    variant="unstyled"
                    display="inline-flex"
                    alignItems="center"
                    height="30px"
                    fontSize="12.5px"
                    color={p.textSub}
                    onClick={closeOpenPaper}
                  >
                    <Icon as={HiChevronLeft} boxSize="15px" mr="4px" />
                    All papers
                  </Button>
                  <Text color={p.text} fontSize="13px" fontWeight={600} noOfLines={1}>
                    {openPaper.name}
                  </Text>
                  <Text color={p.textMuted} fontSize="12px" whiteSpace="nowrap">
                    {[openPaper.code, openPaper.exam_series_year].filter(Boolean).join(" · ")}
                  </Text>
                </HStack>
              </HStack>
            ) : (
              <Box mb="14px">
                <Input
                  value={paperSearch}
                  onChange={(e) => setPaperSearch(e.target.value)}
                  placeholder="Search papers, e.g. paper 2 may 2024"
                  height="38px"
                  width="100%"
                  {...inputStyle}
                />
              </Box>
            )
          ) : (
          <HStack spacing="8px" mb="14px">
            <Input
              value={browseSearch}
              onChange={(e) => setBrowseSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchQuestionBank() } }}
              placeholder="Search the question bank…"
              height="38px"
              flex="1"
              {...inputStyle}
            />
            <Button
              variant="unstyled"
              display="inline-flex"
              alignItems="center"
              px="18px"
              height="38px"
              borderRadius="full"
              bg={p.accent}
              color={p.onAccent}
              fontSize="13px"
              fontWeight={600}
              _hover={{ bg: p.accentHover }}
              onClick={searchQuestionBank}
            >
              Search
            </Button>
          </HStack>
          )}

          {/* Topic filtering belongs to the bank: inside a paper the teacher
              has already chosen the scope. */}
          {questionSource === "bank" && topics.length > 0 && (
            <Box mb="14px" border="1px solid" borderColor={p.border} borderRadius="10px" overflow="hidden">
              <HStack
                justifyContent="space-between"
                padding="10px 12px"
                cursor="pointer"
                background={p.surfaceSubtle}
                onClick={() => setTopicsOpen((v) => !v)}
              >
                <HStack spacing="8px">
                  <Icon as={topicsOpen ? HiChevronUp : HiChevronDown} boxSize="15px" color={p.textMuted} />
                  <Text color={p.text} fontSize="12.5px" fontWeight={600}>Filter by topic</Text>
                  {selectedTopicIds.length > 0 && (
                    <Box background={p.accentSoft} color={p.accentSoftText} fontSize="10.5px" fontWeight={600} px="8px" py="2px" borderRadius="full">
                      {selectedTopicIds.length} selected
                    </Box>
                  )}
                </HStack>
                <Text color={p.textMuted} fontSize="11px">{topics.length} topics</Text>
              </HStack>

              <Collapse in={topicsOpen} animateOpacity>
                <Box padding="12px">
                  <Box display="flex" flexWrap="wrap" gap="8px">
                    {topics.map((topic) => (
                      <TopicPill
                        key={topic.guid}
                        label={topic.name}
                        isSelected={selectedTopicIds.includes(topic.guid)}
                        onClick={() => toggleTopic(topic.guid)}
                      />
                    ))}
                  </Box>
                  <Text color={p.textMuted} fontSize="11.5px" mt="10px">
                    Selecting topics narrows the results. Press Search to apply.
                  </Text>
                </Box>
              </Collapse>
            </Box>
          )}

          {browseError && (
            <Box background={p.accentSoft} borderRadius="8px" padding="10px 14px" mb="10px">
              <Text color={p.accentSoftText} fontSize="12.5px">{browseError}</Text>
            </Box>
          )}

          {/* Browsing papers, none opened yet: the list of papers to open. */}
          {questionSource === "paper" && !openPaper ? (
            papersLoading ? (
              <HStack justifyContent="center" py="24px">
                <Spinner size="sm" color={p.accent} />
                <Text color={p.textSub} fontSize="13px">Loading papers…</Text>
              </HStack>
            ) : visiblePapers.length === 0 ? (
              // Nothing to say when the load itself failed: the error above
              // already explains the empty list.
              browseError ? null : (
                <Text color={p.textMuted} fontSize="13px" textAlign="center" py="20px">
                  {paperResults.length === 0
                    ? "No papers available for this subject yet."
                    : "No papers match that search."}
                </Text>
              )
            ) : (
              <VStack spacing="8px" align="stretch" maxHeight="320px" overflowY="auto" pr="4px">
                {visiblePapers.map((paper) => (
                  <HStack
                    key={paper.guid}
                    as="button"
                    justifyContent="space-between"
                    textAlign="left"
                    padding="12px 14px"
                    border="1px solid"
                    borderColor={p.border}
                    borderRadius="10px"
                    background={p.surface}
                    _hover={{ borderColor: p.accent, background: p.hover }}
                    transition="border-color 0.15s, background 0.15s"
                    onClick={() => openPaperQuestions(paper)}
                  >
                    <Box minWidth={0}>
                      <Text color={p.text} fontSize="13px" fontWeight={600} noOfLines={1}>
                        {paper.name}
                      </Text>
                      <Text color={p.textMuted} fontSize="11.5px" noOfLines={1}>
                        {[
                          paper.code,
                          [paper.exam_series_month, paper.exam_series_year].filter(Boolean).join(" "),
                          paper.total_marks ? `${paper.total_marks} marks` : null,
                        ].filter(Boolean).join("  ·  ")}
                      </Text>
                    </Box>
                    <Icon as={HiChevronRight} boxSize="16px" color={p.textMuted} flexShrink={0} />
                  </HStack>
                ))}
              </VStack>
            )
          ) : browseLoading ? (
            <HStack justifyContent="center" py="24px">
              <Spinner size="sm" color={p.accent} />
              <Text color={p.textSub} fontSize="13px">Searching…</Text>
            </HStack>
          ) : browseResults.length === 0 ? (
            <Text color={p.textMuted} fontSize="13px" textAlign="center" py="20px">
              {questionSource === "paper"
                ? "This paper has no questions in the bank yet."
                : "No questions found. Try a different search or topic."}
            </Text>
          ) : (
            <VStack spacing="8px" align="stretch" maxHeight="320px" overflowY="auto" pr="4px">
              {browseResults.map((group, i) => {
                const added = addedGroups.has(group.group_guid)
                return (
                  <GroupCard
                    key={group.group_guid}
                    group={group}
                    index={i}
                    onAction={() => addGroup(group)}
                    actionIcon={added ? HiOutlineCheck : HiOutlinePlus}
                    actionLabel={added ? "Already added" : "Add question"}
                    actionColor={added ? p.textMuted : p.accent}
                    actionDisabled={added}
                    showNumber={false}
                    listKey="browse"
                    defaultOpen
                  />
                )
              })}
            </VStack>
          )}
        </Box>

        <Box {...card}>
          <HStack justifyContent="space-between" alignItems="center" mb="12px">
            <Text {...cardTitle} marginBottom="0">Questions in this paper</Text>
            <HStack spacing="8px">
              <Box background={p.accentSoft} color={p.accentSoftText} fontSize="11px" fontWeight={600} px="10px" py="4px" borderRadius="full">
                {paperGroups.length} question{paperGroups.length === 1 ? "" : "s"}
              </Box>
              {totalMarks > 0 && (
                <Box background={p.surfaceSubtle} border="1px solid" borderColor={p.border} color={p.textSub} fontSize="11px" fontWeight={600} px="10px" py="4px" borderRadius="full">
                  {totalMarks} marks
                </Box>
              )}
            </HStack>
          </HStack>

          {paperGroups.length === 0 ? (
            <Text color={p.textMuted} fontSize="13px" textAlign="center" py="18px">
              No questions added yet. Search above and add the ones you want.
            </Text>
          ) : (
            <VStack spacing="8px" align="stretch" maxHeight="260px" overflowY="auto" pr="4px">
              {paperGroups.map((group, i) => (
                <GroupCard
                  key={group.group_guid}
                  group={group}
                  index={i}
                  onAction={() => removeGroup(group.group_guid)}
                  actionIcon={HiOutlineTrash}
                  actionLabel="Remove question"
                  actionColor={p.textMuted}
                  listKey="manual-paper"
                  defaultOpen={false}
                />
              ))}
            </VStack>
          )}
        </Box>

        <Box {...card}>
          <Text {...cardTitle}>Due date</Text>
          <ClassroomDatePicker value={dueDate} onChange={setDueDate} p={p} />
        </Box>

        <HStack width="100%" justifyContent="space-between" alignItems="center">
          <Button
            variant="ghost"
            leftIcon={<ChevronLeftIcon />}
            onClick={() => setStep(0)}
            color={p.textSub}
            fontSize="14px"
            fontWeight={500}
            _hover={{ bg: p.hover }}
          >
            Back
          </Button>
          <Button
            variant="unstyled"
            display="inline-flex"
            alignItems="center"
            gap="8px"
            px="22px"
            height="40px"
            borderRadius="full"
            bg={p.accent}
            color={p.onAccent}
            fontSize="14px"
            fontWeight={600}
            opacity={paperGroups.length === 0 ? 0.6 : 1}
            isDisabled={paperGroups.length === 0}
            _hover={{ bg: p.accentHover }}
            onClick={() => setStep(2)}
          >
            Continue to preview <ChevronRightIcon />
          </Button>
        </HStack>
      </VStack>
    )
  }

  // Read-only render of the paper as a student sees it on the solve page:
  // sequential numbering, question text with inline images, and the answer area.
  const renderStudentView = () => (
    <VStack spacing="16px" align="stretch">
      {paperGroups.length === 0 ? (
        <Text color={p.textMuted} fontSize="13px" textAlign="center" py="24px">
          No questions in this paper yet.
        </Text>
      ) : (
        paperGroups.map((group, gi) => {
          const questions = group.questions || []
          const groupMarks = questions.reduce((s, q) => s + (q?.total_mark || 0), 0)
          return (
            <Box
              key={group.group_guid}
              border="1px solid"
              borderColor={p.border}
              borderRadius="12px"
              background={p.surface}
              padding="20px 22px"
            >
              <HStack justifyContent="space-between" alignItems="baseline" mb="14px">
                <Text color={p.text} fontSize="15px" fontWeight={700}>
                  Question {gi + 1}
                </Text>
                {groupMarks > 0 && (
                  <Text color={p.textSub} fontSize="12px" fontWeight={600}>
                    [{groupMarks}]
                  </Text>
                )}
              </HStack>

              <VStack align="stretch" spacing="18px">
                {questions.map((q) => {
                  const part = `${q.letter || ""}${q.letter_part || ""}`
                  const options = parseAnswerFormat(q.answer_format)
                  return (
                    <HStack key={q.guid} align="flex-start" spacing="12px">
                      {part && (
                        <Text color={p.text} fontSize="13.5px" fontWeight={600} minWidth="30px" pt="1px">
                          ({part})
                        </Text>
                      )}
                      <Box flex="1" minWidth="0">
                        <HStack align="flex-start" justifyContent="space-between" spacing="12px">
                          <Box color={p.text} fontSize="13.5px" lineHeight="21px" flex="1" minWidth="0">
                            {buildQuestionNodes(q, p, { maxH: "360px" })}
                          </Box>
                          {q.total_mark > 0 && (
                            <Text color={p.textSub} fontSize="12px" fontWeight={600} flexShrink={0}>
                              [{q.total_mark}]
                            </Text>
                          )}
                        </HStack>

                        {/* Answer area, as it appears on the solve page */}
                        {options.length > 0 ? (
                          <VStack align="stretch" spacing="6px" mt="10px">
                            {options.map((opt) => (
                              <HStack
                                key={opt.label}
                                spacing="10px"
                                padding="8px 12px"
                                borderRadius="8px"
                                border="1px solid"
                                borderColor={p.border}
                                background={p.surfaceSubtle}
                              >
                                <Box
                                  boxSize="14px"
                                  borderRadius="full"
                                  border="1.5px solid"
                                  borderColor={p.textMuted}
                                  flexShrink={0}
                                />
                                <Text color={p.text} fontSize="13px">
                                  {opt.label}{opt.text ? ` ${opt.text}` : ""}
                                </Text>
                              </HStack>
                            ))}
                          </VStack>
                        ) : q.total_mark > 0 ? (
                          <Box
                            mt="10px"
                            height="72px"
                            borderRadius="8px"
                            border="1px dashed"
                            borderColor={p.border}
                            background={p.surfaceSubtle}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text color={p.textMuted} fontSize="11.5px">
                              Student's answer space
                            </Text>
                          </Box>
                        ) : null}
                      </Box>
                    </HStack>
                  )
                })}
              </VStack>
            </Box>
          )
        })
      )}
    </VStack>
  )

  const renderPreview = () => (
    <VStack spacing="20px" align="stretch" width="100%">
      <Box {...card}>
        <HStack justifyContent="space-between" alignItems="flex-start" marginBottom="4px">
          <Box>
            <Text color={p.text} fontSize="14px" fontWeight={600}>
              {title || "Untitled paper"}
            </Text>
            <Text color={p.textMuted} fontSize="12px" mt="2px">
              {previewView === "list"
                ? "Review the questions below. Remove any you do not want, or add more, then assign."
                : "This is how the paper will look to students on the solve page."}
            </Text>
          </Box>
          <HStack spacing="8px" flexShrink={0}>
            <Box background={p.accentSoft} color={p.accentSoftText} fontSize="11px" fontWeight={600} px="10px" py="4px" borderRadius="full">
              {paperGroups.length} question{paperGroups.length === 1 ? "" : "s"}
            </Box>
            <Box background={p.surfaceSubtle} border="1px solid" borderColor={p.border} color={p.textSub} fontSize="11px" fontWeight={600} px="10px" py="4px" borderRadius="full">
              {totalMarks} marks
            </Box>
            {dueDate && (
              <Box background={p.surfaceSubtle} border="1px solid" borderColor={p.border} color={p.textSub} fontSize="11px" fontWeight={600} px="10px" py="4px" borderRadius="full">
                Due {formatDMY(dueDate)}
              </Box>
            )}
          </HStack>
        </HStack>

        {previewError && (
          <Box background={p.accentSoft} borderRadius="8px" padding="10px 14px" my="10px">
            <Text color={p.accentSoftText} fontSize="12.5px">{previewError}</Text>
          </Box>
        )}

        {/* Switch between managing the list and seeing it as a student would */}
        <HStack spacing="0" mt="14px" width="fit-content" borderRadius="full" border="1px solid" borderColor={p.border} background={p.surfaceSubtle} padding="3px">
          {[
            { key: "list", label: "List" },
            { key: "student", label: "Student view" },
          ].map((opt) => {
            const active = previewView === opt.key
            return (
              <Box
                key={opt.key}
                as="button"
                type="button"
                px="16px"
                height="30px"
                borderRadius="full"
                fontSize="12.5px"
                fontWeight={600}
                background={active ? p.accent : "transparent"}
                color={active ? p.onAccent : p.textSub}
                transition="all 0.15s"
                _hover={{ color: active ? p.onAccent : p.text }}
                onClick={() => setPreviewView(opt.key)}
              >
                {opt.label}
              </Box>
            )
          })}
        </HStack>

        {previewView === "student" ? (
          <Box mt="14px" maxHeight="520px" overflowY="auto" pr="4px">
            {renderStudentView()}
          </Box>
        ) : (
        <VStack spacing="8px" align="stretch" mt="14px" maxHeight="340px" overflowY="auto" pr="4px">
          {paperQuestions.length === 0 && !previewError ? (
            <Text color={p.textMuted} fontSize="13px" textAlign="center" py="20px">
              No questions matched these settings. Go back and widen the topics or difficulty.
            </Text>
          ) : (
            paperGroups.map((group, i) => (
              <GroupCard
                key={group.group_guid}
                group={group}
                index={i}
                onAction={() => removeGroup(group.group_guid)}
                actionIcon={HiOutlineTrash}
                actionLabel="Remove question"
                actionColor={p.textMuted}
                listKey="preview"
                defaultOpen={false}
              />
            ))
          )}
        </VStack>
        )}

        <Box mt="14px" display={previewView === "student" ? "none" : "block"}>
          {!showCandidates ? (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Icon as={HiOutlinePlus} boxSize="14px" />}
              onClick={loadCandidates}
              color={p.textSub}
              fontSize="12.5px"
              fontWeight={500}
              _hover={{ bg: p.hover }}
            >
              Add more questions
            </Button>
          ) : (
            <Box border="1px dashed" borderColor={p.border} borderRadius="10px" padding="12px" background={p.surfaceSubtle}>
              <Text color={p.textSub} fontSize="12px" fontWeight={600} mb="8px">
                Matching questions you can add
              </Text>
              {candidates.length === 0 ? (
                <Text color={p.textMuted} fontSize="12.5px">No further matching questions found.</Text>
              ) : (
                <VStack spacing="8px" align="stretch" maxHeight="220px" overflowY="auto" pr="4px">
                  {candidates.map((group, i) => (
                    <GroupCard
                      key={group.group_guid}
                      group={group}
                      index={i}
                      onAction={() => addGroup(group)}
                      actionIcon={HiOutlinePlus}
                      actionLabel="Add question"
                      actionColor={p.accent}
                      showNumber={false}
                      listKey="candidates"
                      defaultOpen={false}
                    />
                  ))}
                </VStack>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Assign to: everyone or a chosen set of students */}
      <Box {...card}>
        <Text {...cardTitle} marginBottom="12px">Assign to</Text>
        <HStack spacing="8px" mb={assignMode === "selected" ? "14px" : "0"}>
          {[
            { key: "all", label: `Everyone${students.length ? ` (${students.length})` : ""}` },
            { key: "selected", label: "Specific students" },
          ].map((opt) => {
            const active = assignMode === opt.key
            return (
              <Button
                key={opt.key}
                variant="unstyled"
                display="inline-flex"
                alignItems="center"
                px="16px"
                height="34px"
                borderRadius="full"
                fontSize="12.5px"
                fontWeight={600}
                border="1px solid"
                borderColor={active ? p.accent : p.border}
                background={active ? p.accent : p.surfaceSubtle}
                color={active ? p.onAccent : p.text}
                _hover={{ borderColor: active ? p.accentHover : p.textMuted }}
                onClick={() => setAssignMode(opt.key)}
              >
                {opt.label}
              </Button>
            )
          })}
        </HStack>

        {assignMode === "selected" && (
          <Box>
            <HStack justifyContent="space-between" mb="8px">
              <Text color={p.textMuted} fontSize="11.5px">
                {selectedStudentGuids.length} of {students.length} selected
              </Text>
              <Button
                variant="ghost"
                size="xs"
                color={p.accentSoftText}
                fontSize="11.5px"
                _hover={{ bg: p.hover }}
                onClick={() =>
                  setSelectedStudentGuids(
                    selectedStudentGuids.length === students.length
                      ? []
                      : students.map((s) => s.user_guid)
                  )
                }
              >
                {selectedStudentGuids.length === students.length ? "Clear all" : "Select all"}
              </Button>
            </HStack>
            {students.length === 0 ? (
              <Text color={p.textMuted} fontSize="12.5px" fontStyle="italic">
                No students enrolled in this space yet.
              </Text>
            ) : (
              <VStack align="stretch" spacing="2px" maxHeight="180px" overflowY="auto" pr="4px">
                {students.map((s) => (
                  <Checkbox
                    key={s.user_guid}
                    isChecked={selectedStudentGuids.includes(s.user_guid)}
                    onChange={() => toggleStudent(s.user_guid)}
                    sx={{
                      "& .chakra-checkbox__control": {
                        borderColor: p.border,
                        background: p.inputBg,
                        _checked: { background: p.accent, borderColor: p.accent, color: p.onAccent },
                      },
                    }}
                    py="6px"
                    px="8px"
                    borderRadius="8px"
                    _hover={{ background: p.hover }}
                  >
                    <Text fontSize="13px" color={p.text}>{studentName(s)}</Text>
                  </Checkbox>
                ))}
              </VStack>
            )}
          </Box>
        )}
      </Box>

      <HStack width="100%" justifyContent="space-between" alignItems="center">
        <Button
          variant="ghost"
          leftIcon={<ChevronLeftIcon />}
          onClick={handleBackFromPreview}
          color={p.textSub}
          fontSize="14px"
          fontWeight={500}
          _hover={{ bg: p.hover }}
        >
          Back
        </Button>
        <Button
          onClick={handleAssign}
          isDisabled={assignDisabled}
          opacity={assignDisabled ? 0.5 : 1}
          variant="unstyled"
          display="inline-flex"
          alignItems="center"
          px="20px"
          height="40px"
          borderRadius="full"
          background={p.accent}
          color={p.onAccent}
          fontSize="13px"
          fontWeight={600}
          _hover={{ background: p.accentHover }}
        >
          {assignLabel}
        </Button>
      </HStack>
    </VStack>
  )

  const steps = ["Details", buildMode === "manual" ? "Questions" : "Settings", "Preview"]

  return (
    <Box
      className="stande-classroom"
      data-theme={isDark ? "dark" : "light"}
      minHeight="100vh"
      background={p.page}
      transition="background 0.2s"
    >
      <Box maxWidth="1120px" mx="auto" padding={{ base: "28px 20px", md: "40px 48px 56px" }}>
        {/* Back to the classroom */}
        <HStack
          spacing="8px"
          mb="24px"
          cursor={loadingPhase === "idle" ? "pointer" : "not-allowed"}
          opacity={loadingPhase === "idle" ? 1 : 0.5}
          width="fit-content"
          onClick={handleClose}
        >
          <ChevronLeftIcon color={p.textSub} />
          <Text color={p.textSub} fontSize="14px" fontWeight={500}>
            Back to space
          </Text>
        </HStack>

        <VStack spacing="22px" align="stretch">
            <Box textAlign="center">
              <Text color={p.text} fontSize="22px" fontWeight={600} marginBottom="4px" letterSpacing="-0.3px">
                Create a custom paper
              </Text>
              <Text color={p.textSub} fontSize="13px" fontWeight={400}>
                Choose topics, difficulty and length, then preview the questions before assigning.
              </Text>
            </Box>

            {/* step indicator */}
            {loadingPhase === "idle" && (
              <HStack spacing="8px" justifyContent="center">
                {steps.map((label, i) => (
                  <HStack key={label} spacing="8px">
                    <HStack spacing="6px">
                      <Box
                        w="20px" h="20px" borderRadius="full"
                        display="flex" alignItems="center" justifyContent="center"
                        fontSize="11px" fontWeight={600}
                        bg={i <= step ? p.accent : p.hover}
                        color={i <= step ? p.onAccent : p.textMuted}
                      >
                        {i + 1}
                      </Box>
                      <Text fontSize="12px" fontWeight={500} color={i === step ? p.text : p.textMuted}>{label}</Text>
                    </HStack>
                    {i < steps.length - 1 && <Box w="20px" h="1px" bg={p.border} />}
                  </HStack>
                ))}
              </HStack>
            )}

            {loadingPhase !== "idle"
              ? renderLoadingState()
              : step === 0
              ? renderStepOne()
              : step === 1
              ? (buildMode === "manual" ? renderManualBuild() : renderStepTwo())
              : renderPreview()}
        </VStack>
      </Box>
    </Box>
  )
}
